import { useLeadForm, leadField } from "@/lib/leadForms";
import { API } from "@/lib/api";
import { useEffect, useRef, useState } from "react";
import { Clock, Video, UserCheck, BriefcaseBusiness, GraduationCap, Layers, Wrench, BadgeCheck, CalendarClock, Play, Check, X, Linkedin, ArrowRight, Download, ChevronLeft, ChevronRight, Monitor, Users, Building2, Cpu, Award, MessageSquare, FolderKanban, BookOpen, Briefcase, Phone, Plus, Minus, ThumbsUp, ThumbsDown, Network, IdCard, Rocket, HandCoins, Sparkles, Quote, Star } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const NAVY = "#0F1115";
const RED = "#F5B301";
const BLUE = "#2563EB";
export const TEMPLATE_NAV_LABELS = {
  t_highlights: "Program Highlights",
  t_curriculum: "Curriculum",
  t_projects: "Projects",
  t_certification: "Certification",
  t_faculty: "Faculty",
  t_testimonials: "Testimonials",
  t_employers: "Where Learners Work",
  t_admission: "Admission",
  t_fee: "Fees",
  t_compare: "Compare",
  t_about: "About",
};

const navLabelOf = (s) => {
  if (!(s.type in TEMPLATE_NAV_LABELS)) return null;
  if (s.nav_label != null) { const t = String(s.nav_label).trim(); return t || null; }
  return TEMPLATE_NAV_LABELS[s.type];
};

function TemplateNavTabs({ tabs }) {
  const [active, setActive] = useState(tabs[0]?.id || "");
  const barRef = useRef(null);
  const idsKey = tabs.map((t) => t.id).join(",");

  useEffect(() => {
    const list = idsKey.split(",").filter(Boolean);
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) setActive(en.target.id.replace("tpl-sec-", "")); });
    }, { rootMargin: "-170px 0px -55% 0px", threshold: 0 });
    list.forEach((id) => { const el = document.getElementById(`tpl-sec-${id}`); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [idsKey]);

  useEffect(() => {
    const btn = barRef.current?.querySelector(`[data-tab="${active}"]`);
    if (btn) btn.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
  }, [active]);

  const jump = (id) => {
    setActive(id);
    const el = document.getElementById(`tpl-sec-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="sticky top-[84px] z-40 px-3 sm:px-5" data-testid="tpl-nav">
      <div ref={barRef}
        className="max-w-7xl mx-auto flex items-center gap-1 overflow-x-auto whitespace-nowrap bg-white/95 backdrop-blur-md border border-slate-200/80 rounded-full shadow-lg shadow-slate-900/5 px-2 py-2"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        role="navigation" aria-label="Page sections">
        {tabs.map((t) => (
          <button key={t.id} type="button" data-tab={t.id} onClick={() => jump(t.id)}
            className={`shrink-0 rounded-full px-4 sm:px-5 py-2.5 text-sm font-bold transition-colors ${t.hideCls || ""} ${active === t.id ? "bg-[#FFC72C] text-[#17263B]" : "text-slate-700 hover:bg-slate-100"}`}
            data-testid={`tpl-nav-tab-${t.id}`} aria-current={active === t.id ? "true" : undefined}>
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}


const CHIP_ICONS = [Clock, Video, UserCheck, BriefcaseBusiness];
const HIGHLIGHT_ICONS = { graduation: GraduationCap, layers: Layers, wrench: Wrench, briefcase: Briefcase, badge: BadgeCheck, calendar: CalendarClock, coins: HandCoins, network: Network, book: BookOpen, sparkle: Sparkles };
const AUDIENCE_ICONS = [GraduationCap, Users, Building2, Cpu];
const INCLUDED_ICONS = [BookOpen, Monitor, FolderKanban, Award, MessageSquare, Briefcase];

const toEmbedUrl = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?autoplay=1`;
  return null;
};

const DEFAULT_AVATARS = [
  "https://i.pravatar.cc/64?img=32",
  "https://i.pravatar.cc/64?img=47",
  "https://i.pravatar.cc/64?img=12",
  "https://i.pravatar.cc/64?img=5",
];
const FEATURE_ICONS = [Monitor, FolderKanban, Wrench, MessageSquare];

const Script = ({ children, color = NAVY, className = "" }) => (
  <span className={className} style={{ fontFamily: "'Caveat', cursive", color }}>{children}</span>
);

function useVisibleCount() {
  const [n, setN] = useState(3);
  useEffect(() => {
    const update = () => setN(window.innerWidth < 640 ? 1 : window.innerWidth < 1024 ? 2 : 3);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return n;
}

function SectionHead({ title, subtitle, linkLabel, linkUrl }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-5">
      <div>
        <h2 className="text-2xl font-extrabold tracking-tight" style={{ color: NAVY }}>{title}</h2>
        {subtitle && <p className="text-sm sm:text-base text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {linkLabel && (
        <a href={linkUrl || "#"} className="text-xs font-bold flex items-center gap-1 shrink-0 mt-1 hover:underline" style={{ color: BLUE }}>
          {linkLabel} <ArrowRight className="w-3.5 h-3.5" />
        </a>
      )}
    </div>
  );
}

function CtaButtons({ c, onApply, onBrochure, onCallback, dark = false }) {
  return (
    <div className="flex flex-wrap gap-3 items-center">
      <button onClick={onBrochure} data-testid="tpl-brochure-btn"
        className={`px-6 py-3 rounded-lg border font-semibold text-sm flex items-center gap-2 transition-colors ${dark ? "border-white/50 text-white hover:bg-white/10" : "border-slate-300 text-slate-700 bg-white hover:border-slate-400"}`}>
        <Download className="w-4 h-4" /> {c.cta_secondary || "Download Brochure"}
      </button>
      <button onClick={onApply} data-testid="tpl-apply-btn"
        className="px-8 py-3 rounded-lg font-bold text-sm text-[#0F1115] transition-colors hover:opacity-90" style={{ background: RED }}>
        {c.cta_primary || "Apply Now"}
      </button>
      {onCallback && (
        <button onClick={onCallback} data-testid="tpl-callback-btn"
          className={`px-2 py-3 font-semibold text-sm underline underline-offset-4 transition-colors ${dark ? "text-white/80 hover:text-white" : "text-slate-600 hover:text-slate-900"}`}>
          Request a callback
        </button>
      )}
    </div>
  );
}

function THero({ c, onApply, onBrochure, onCallback }) {
  const features = c.features || [];
  return (
    <section className="bg-white border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center lg:items-stretch">
        <div className="lg:self-center">
          <p className="text-xs font-semibold flex items-center gap-1.5 mb-4" style={{ color: BLUE }}>
            <span className="border rounded px-1.5 py-0.5" style={{ borderColor: BLUE }}>{c.crumb_root}</span>
            <ChevronRight className="w-3 h-3" /> {c.crumb_category}
          </p>
          <h1 className="text-3xl sm:text-[42px] font-extrabold leading-[1.1] tracking-tight" style={{ color: NAVY }}>
            {c.title_line1}<br />{c.title_line2}
          </h1>
          <p className="text-lg text-slate-600 mt-3">{c.tagline}</p>
          <div className="flex flex-wrap gap-x-5 gap-y-2 mt-5">
            {(c.chips || []).map((chip, i) => {
              const Icon = CHIP_ICONS[i % CHIP_ICONS.length];
              return <span key={i} className="flex items-center gap-1.5 text-xs font-semibold text-slate-600"><Icon className="w-4 h-4" style={{ color: NAVY }} />{chip}</span>;
            })}
          </div>
          <div className="mt-7"><CtaButtons c={c} onApply={onApply} onBrochure={onBrochure} onCallback={onCallback} /></div>
        </div>
        <div className="relative w-full max-w-xl lg:justify-self-end" data-testid="tpl-hero-media">
          <div aria-hidden className="absolute -top-3 -right-3 w-2/3 h-2/3 rounded-[28px]" style={{ background: NAVY }} />
          <div aria-hidden className="absolute -top-3 -right-3 w-24 h-24 rounded-tr-[28px]" style={{ backgroundImage: "radial-gradient(rgba(245,179,1,0.6) 1.6px, transparent 1.6px)", backgroundSize: "13px 13px" }} />
          <div aria-hidden className="absolute -bottom-3 -left-3 w-1/2 h-2/5 rounded-[28px]" style={{ background: NAVY }} />
          <div className="relative z-10 rounded-[24px] border border-slate-200 bg-white shadow-xl overflow-hidden lg:h-full lg:flex lg:flex-col" data-testid="tpl-hero-frame">
            <div className="bg-slate-100 aspect-[16/10] lg:aspect-auto lg:flex-1 lg:min-h-[200px] lg:relative">
              {c.image && <img src={c.image} alt={c.image_alt || c.title_line2 || "Program"} title={c.image_title || undefined} className="w-full h-full object-cover lg:absolute lg:inset-0" />}
            </div>
            <div className="border-t border-slate-100" data-testid="tpl-hero-mediabar">
              {c.script_text && (
                <Script className="block text-center text-xl font-bold py-1.5 border-b border-slate-100" data-testid="tpl-hero-script">{c.script_text}</Script>
              )}
              <div className="flex flex-wrap gap-px bg-slate-100 border-t border-slate-100">
                {features.map((f, i) => {
                  const Icon = FEATURE_ICONS[i % FEATURE_ICONS.length];
                  return (
                    <span key={i} title={f} className="bg-white flex-1 basis-[calc(50%-1px)] sm:basis-[calc(33.333%-1px)] min-w-0 flex items-center justify-center gap-1.5 px-3 py-2.5 text-[11px] font-bold text-center leading-tight" style={{ color: NAVY }} data-testid={`tpl-hero-stat-${i}`}>
                      <Icon className="w-4 h-4 shrink-0" style={{ color: RED }} /> <span>{f}</span>
                    </span>
                  );
                })}
                {(c.learners_count || c.learners_label) && (
                  <span className="bg-white flex-1 basis-[calc(50%-1px)] sm:basis-[calc(33.333%-1px)] min-w-0 flex items-center justify-center gap-2.5 px-3 py-2" data-testid="tpl-hero-learners">
                    <span className="flex -space-x-2 shrink-0" data-testid="tpl-hero-avatars">
                      {(Array.isArray(c.learners_avatars) && c.learners_avatars.length ? c.learners_avatars : DEFAULT_AVATARS).slice(0, 4).map((av, i) => (
                        <img key={i} src={av} alt={`Learner ${i + 1}`} title={`Learner ${i + 1}`}
                          className="w-7 h-7 rounded-full border-2 border-white object-cover shadow-sm" data-testid={`tpl-hero-avatar-${i}`} />
                      ))}
                    </span>
                    <span className="text-left shrink-0">
                      <span className="block text-sm font-extrabold leading-tight" style={{ color: NAVY }}>{c.learners_count}</span>
                      <span className="block text-[9px] font-bold uppercase tracking-wider text-slate-400">{c.learners_label}</span>
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TLogos({ c }) {
  return (
    <section className="border-b border-slate-100 bg-white">
      <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap items-center gap-x-8 gap-y-3">
        <span className="text-xs font-bold" style={{ color: NAVY }}>{c.label}</span>
        {(c.logos || []).map((l, i) => (
          l.image ? (
            <img key={i} src={l.image} alt={l.name || "Partner logo"} title={l.name || ""}
              className="h-7 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity" data-testid={`tpl-logo-${i}`} />
          ) : (
            <span key={i} className="text-lg font-extrabold tracking-tight" style={{ color: l.color || "#334155" }} data-testid={`tpl-logo-${i}`}>{l.name}</span>
          )
        ))}
      </div>
    </section>
  );
}

const TONE_CYCLE = ["red", "blue", "amber", "green", "purple", "pink"];
const TONE_STYLES = {
  red: { bg: "#FEE2E2", fg: "#DC2626" }, blue: { bg: "#DBEAFE", fg: "#2563EB" },
  amber: { bg: "#FEF3C7", fg: "#D97706" }, green: { bg: "#D1FAE5", fg: "#059669" },
  purple: { bg: "#EDE9FE", fg: "#7C3AED" }, pink: { bg: "#FCE7F3", fg: "#DB2777" },
};

function THighlights({ c }) {
  const cards = (c.cards || []).filter((card) => !card.hidden);
  const PER_PAGE = 6;
  const pages = Math.max(1, Math.ceil(cards.length / PER_PAGE));
  const [page, setPage] = useState(0);
  const visible = cards.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);
  return (
    <section className="max-w-7xl mx-auto px-6 py-12 sm:py-14" data-testid="tpl-highlights">
      <span className="block w-10 h-1 rounded-full mb-4" style={{ background: "#F5C518" }} />
      <h2 className="text-3xl font-extrabold tracking-tight" style={{ color: NAVY }}>
        {c.title_black || c.title || "Program"}{" "}
        {c.title_red && <span style={{ color: RED }}>{c.title_red}</span>}
      </h2>
      {c.subtitle && <p className="text-base text-slate-500 mt-2">{c.subtitle}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mt-7">
        {visible.map((card, i) => {
          const Icon = HIGHLIGHT_ICONS[card.icon] || GraduationCap;
          const tone = TONE_STYLES[card.tone] || TONE_STYLES[TONE_CYCLE[(page * PER_PAGE + i) % TONE_CYCLE.length]];
          return (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm hover:shadow-md hover:border-red-200 transition-shadow flex items-start gap-4 sm:block" data-testid={`tpl-highlight-card-${page * PER_PAGE + i}`}>
              <span className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 sm:mb-4" style={{ background: tone.bg }}>
                <Icon className="w-5 h-5" style={{ color: tone.fg }} />
              </span>
              <div className="min-w-0">
                <p className="text-sm sm:text-[15px] font-extrabold leading-snug text-slate-900">{[card.line1, card.line2].filter(Boolean).join(" ")}</p>
                {card.desc && <p className="text-xs sm:text-sm text-slate-500 mt-1 leading-relaxed">{card.desc}</p>}
              </div>
            </div>
          );
        })}
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-8" data-testid="tpl-highlights-pagination">
          <button onClick={() => setPage((page - 1 + pages) % pages)} data-testid="tpl-highlights-prev"
            className="w-9 h-9 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:bg-slate-50 transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-extrabold" style={{ color: NAVY }} data-testid="tpl-highlights-page">{page + 1}/{pages}</span>
          <button onClick={() => setPage((page + 1) % pages)} data-testid="tpl-highlights-next"
            className="w-9 h-9 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:bg-slate-50 transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </section>
  );
}

function TCertification({ c }) {
  const legacy = c.certificate_image ? [{ title: c.title, desc: (c.bullets || []).join(" · "), image: c.certificate_image }] : [];
  const certs = (c.certificates || []).length ? c.certificates : legacy;
  const [start, setStart] = useState(0);
  const VISIBLE = 2;
  const maxStart = Math.max(0, certs.length - VISIBLE);
  const visible = certs.slice(start, start + VISIBLE);
  const pages = maxStart + 1;
  return (
    <section className="max-w-7xl mx-auto px-6 py-14" data-testid="tpl-certification">
      <h2 className="text-3xl font-extrabold tracking-tight">
        <span style={{ color: RED }}>{c.title_red || "Certificate"}</span>{" "}
        <span style={{ color: NAVY }}>{c.title_black || c.title || "in Gen AI- Powered Data Science Program"}</span>
      </h2>
      <div className="relative mt-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {visible.map((cert, i) => {
            const idx = start + i;
            return (
              <div key={idx} data-testid={`tpl-cert-${idx}`}>
                <p className="text-xl font-extrabold text-slate-900 leading-snug">{cert.title}</p>
                {cert.desc && <p className="text-base text-slate-600 mt-3 leading-relaxed">{cert.desc}</p>}
                <div className="rounded-xl overflow-hidden border border-slate-200 shadow-md bg-white mt-6">
                  {cert.image && <img src={cert.image} alt={cert.image_alt || cert.title} title={cert.image_title || undefined} className="w-full object-cover" />}
                </div>
              </div>
            );
          })}
        </div>
        {start > 0 && (
          <button onClick={() => setStart(Math.max(0, start - 1))} data-testid="tpl-certification-prev"
            className="absolute left-1 lg:-left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        {start < maxStart && (
          <button onClick={() => setStart(Math.min(maxStart, start + 1))} data-testid="tpl-certification-next"
            className="absolute right-1 lg:-right-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
        {pages > 1 && (
          <div className="flex justify-center gap-2 mt-8" data-testid="tpl-certification-dots">
            {Array.from({ length: pages }).map((_, i) => (
              <button key={i} onClick={() => setStart(i)} data-testid={`tpl-cert-dot-${i}`} aria-label={`Slide ${i + 1}`}
                className={`h-2 rounded-full transition-all ${i === start ? "w-6" : "w-2 bg-slate-300"}`}
                style={i === start ? { background: RED } : {}} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function TCurriculum({ c, onBrochure }) {
  const tabs = c.tabs || [];
  const [expanded, setExpanded] = useState({ 0: true });
  const [previewModal, setPreviewModal] = useState(null);
  const [unlocked, setUnlocked] = useState(false);
  const [pForm, setPForm] = useState({ name: "", email: "", phone: "", experience: "" });
  const [pConsent, setPConsent] = useState(true);
  const [pBusy, setPBusy] = useState(false);
  const [pMsg, setPMsg] = useState("");
  const pcfg = useLeadForm("preview", !!previewModal);
  const pf = (k, fb = {}) => leadField(pcfg, k, fb);
  const playIcon = (cls) => (<svg viewBox="0 0 24 24" fill="currentColor" className={cls}><path d="M8 5v14l11-7z" /></svg>);
  const allPreviews = [];
  tabs.forEach((m) => (m.previews || []).forEach((pv) => { if (pv.video_url) allPreviews.push({ ...pv, module: m.name }); }));
  const findPreview = (mod, topic) => (mod.previews || []).find((pv) => pv.video_url && (pv.topic || "").trim().toLowerCase() === (topic || "").trim().toLowerCase());
  const unlockPreview = async (e) => {
    e.preventDefault();
    const need = (k) => pf(k, { required: true }).visible && pf(k).required;
    if ((need("name") && !pForm.name.trim()) || (need("email") && !pForm.email.trim()) || (need("phone") && !pForm.phone.trim()) || (need("experience") && !pForm.experience)) { setPMsg("Please fill all required fields."); return; }
    if (!pConsent) { setPMsg("Please accept the consent to continue."); return; }
    setPMsg("");
    setPBusy(true);
    try {
      const slug = window.location.pathname.startsWith("/courses/") ? window.location.pathname.split("/")[2] : "";
      if (slug) {
        const res = await fetch(`${API}/inquiries`, {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({
            name: pForm.name.trim(), email: pForm.email.trim(), phone: pForm.phone.trim(), program_slug: slug,
            message: `Preview unlock — ${previewModal?.topic || "curriculum preview"} (${previewModal?.module || ""}) · Work experience: ${pForm.experience} years`,
            page: window.location.pathname, source: "preview_unlock",
          }),
        });
        if (!res.ok) throw new Error("failed");
      }
      setUnlocked(true);
    } catch { setPMsg("Could not unlock right now — please try again."); } finally { setPBusy(false); }
  };
  const hasRail = !!(c.syllabus_label || c.syllabus_pdf || c.call_title || c.call_phone || c.call_note);
  const phoneIcon = (cls) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
  );
  const modules = (
    <div className="mt-8 rounded-2xl border border-slate-200 overflow-hidden" style={{ background: "#EEF3FB" }} data-testid="tpl-curriculum-accordion">
      <div className="relative">
        <div className="absolute left-[30px] sm:left-[38px] top-4 bottom-4 w-px bg-slate-300/70" />
        {tabs.map((mod, i) => {
          const topics = mod.topics || [];
          const open = !!expanded[i];
          return (
            <div key={i} className="relative border-b border-slate-300/50 last:border-0" data-testid={`tpl-curriculum-card-${i}`}>
              <button onClick={() => setExpanded({ ...expanded, [i]: !open })} className="w-full flex items-center gap-4 sm:gap-6 px-5 sm:px-8 py-5 text-left group" data-testid={`tpl-curriculum-readmore-${i}`}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0 relative z-10 transition-colors" style={{ background: open ? RED : "#B6C2D6" }} />
                <span className="flex-1 min-w-0">
                  <span className="block text-lg font-extrabold text-slate-900 group-hover:text-slate-600 transition-colors truncate" data-testid={`tpl-curriculum-module-${i}`}>{/^module\s*\d+/i.test((mod.name || "").trim()) ? mod.name : `Module ${i + 1}: ${mod.name}`}</span>
                </span>
                {mod.topics_label && <span className="hidden sm:inline-block text-[10px] font-bold uppercase tracking-wider text-slate-400 border border-slate-300 rounded-full px-2.5 py-1 shrink-0">{mod.topics_label}</span>}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}><path d="m18 15-6-6-6 6" /></svg>
              </button>
              {open && (
                <div className="px-5 sm:px-8 pb-6 pl-[52px] sm:pl-[76px]" data-testid={`tpl-curriculum-body-${i}`}>
                  {mod.description && <p className="text-sm text-slate-500 leading-relaxed mb-4">{mod.description}</p>}
                  <ul className="space-y-3">
                    {topics.map((t, j) => {
                      const pv = findPreview(mod, t);
                      return (
                        <li key={j} className="flex items-center justify-between gap-4">
                          <span className="flex items-start gap-2.5 text-sm text-slate-600 min-w-0">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1.5 shrink-0" />{t}
                          </span>
                          {pv && (
                            <button onClick={() => { setPreviewModal({ ...pv, topic: t, module: mod.name }); setPMsg(""); }} className="flex flex-col items-center gap-1 shrink-0 group/pv" data-testid={`tpl-curriculum-preview-${i}-${j}`}>
                              <span className="w-9 h-9 rounded-full flex items-center justify-center text-white group-hover/pv:scale-105 transition-transform" style={{ background: "#2563EB" }}>{playIcon("w-3.5 h-3.5 ml-0.5")}</span>
                              <span className="text-[11px] font-bold" style={{ color: "#2563EB" }}>Preview</span>
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
  const rail = (
    <aside className="mt-10 lg:mt-10 space-y-5 self-start lg:sticky lg:top-24" data-testid="tpl-curriculum-rail">
      {(c.syllabus_label || c.syllabus_pdf) && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6" data-testid="tpl-syllabus-card">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "#FEF6E0" }}>
            <Download className="w-5 h-5" style={{ color: RED }} />
          </div>
          <h3 className="text-lg font-extrabold text-slate-900 mt-4">{c.syllabus_label || "Download Syllabus"}</h3>
          <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{c.syllabus_note || "Get the complete module-wise syllabus as a PDF."}</p>
          {c.syllabus_pdf ? (
            <button onClick={() => onBrochure && onBrochure(c.syllabus_pdf || "")}
              className="mt-4 w-full px-5 py-3 rounded-lg font-bold text-sm text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              style={{ background: RED }} data-testid="tpl-syllabus-download">
              <Download className="w-4 h-4" /> Download PDF
            </button>
          ) : (
            <p className="mt-4 text-[11px] font-semibold text-slate-400 border border-dashed border-slate-200 rounded-lg px-3 py-2.5 text-center" data-testid="tpl-syllabus-pending">Syllabus PDF will be available here soon</p>
          )}
        </div>
      )}
      {(c.call_title || c.call_phone || c.call_note) && (
        <div className="rounded-2xl p-6 text-white" style={{ background: NAVY }} data-testid="tpl-call-card">
          <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
            {phoneIcon("w-5 h-5 text-[#F5B301]")}
          </div>
          <h3 className="text-lg font-extrabold mt-4">{c.call_title || "Have questions? Talk to a counselor"}</h3>
          {c.call_note && <p className="text-sm text-slate-300 mt-1.5 leading-relaxed">{c.call_note}</p>}
          {c.call_phone && (
            <a href={`tel:${String(c.call_phone).replace(/[^+\d]/g, "")}`}
              className="mt-4 w-full px-5 py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
              style={{ background: "#F5B301", color: "#0F1115" }} data-testid="tpl-call-phone">
              {phoneIcon("w-4 h-4")} {c.call_phone}
            </a>
          )}
        </div>
      )}
    </aside>
  );
  return (
    <section className="max-w-7xl mx-auto px-6 py-14" data-testid="tpl-curriculum">
      <h2 className="text-3xl font-extrabold tracking-tight" style={{ color: NAVY }}>
        {c.title_black || "Program"} <span style={{ color: RED }}>{c.title_red || "Curriculum"}</span>
      </h2>
      {c.subtitle && <p className="text-base text-slate-500 mt-3 max-w-2xl leading-relaxed">{c.subtitle}</p>}
      {hasRail ? (
        <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-10 items-start">
          <div className="min-w-0 [&>div]:mt-0">{modules}</div>
          {rail}
        </div>
      ) : modules}
      {previewModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(15,17,21,0.78)" }} onClick={() => setPreviewModal(null)}>
          <div className="w-full max-w-5xl rounded-2xl overflow-hidden shadow-2xl" style={{ background: "#151A23" }} onClick={(e) => e.stopPropagation()} data-testid="tpl-preview-modal">
            <div className="flex items-center justify-between px-5 h-12 border-b border-white/10">
              <p className="text-sm font-extrabold text-white">Free Previews</p>
              <button onClick={() => setPreviewModal(null)} className="text-slate-400 hover:text-white p-1" data-testid="tpl-preview-close" aria-label="Close preview">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-5 h-5"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="grid lg:grid-cols-[minmax(0,1fr)_320px]">
              <div className="relative">
                {unlocked ? (
                  toEmbedUrl(previewModal.video_url) ? (
                    <iframe key={previewModal.video_url} src={toEmbedUrl(previewModal.video_url)} className="w-full aspect-video bg-black" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title="Preview video" data-testid="tpl-preview-video" />
                  ) : (
                    <video key={previewModal.video_url} src={previewModal.video_url} controls autoPlay className="w-full aspect-video bg-black" data-testid="tpl-preview-video" />
                  )
                ) : (
                  <div className="aspect-video flex items-center justify-center p-4 sm:p-8 overflow-y-auto" style={{ background: "linear-gradient(135deg,#0B0E14,#1A2130)" }}>
                    <form onSubmit={unlockPreview} className="bg-white rounded-xl p-5 sm:p-6 w-full max-w-sm shadow-2xl my-auto" data-testid="tpl-preview-form">
                      <p className="text-sm font-extrabold text-slate-900">{pcfg.title || "Unlock this free preview"}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5 mb-3 truncate">{previewModal.topic}{previewModal.module ? ` · ${previewModal.module}` : ""}</p>
                      {pf("name", { label: "Name", required: true }).visible && <input required={!!pf("name").required} placeholder={`${pf("name").label || "Name"}${pf("name").required ? "*" : ""}`} value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} className="w-full border-b border-slate-300 py-2 text-sm focus:outline-none focus:border-slate-600" data-testid="tpl-preview-name" />}
                      {pf("email", { label: "Email", required: true }).visible && <input required={!!pf("email").required} type="email" placeholder={`${pf("email").label || "Email"}${pf("email").required ? "*" : ""}`} value={pForm.email} onChange={(e) => setPForm({ ...pForm, email: e.target.value })} className="w-full border-b border-slate-300 py-2 text-sm focus:outline-none focus:border-slate-600" data-testid="tpl-preview-email" />}
                      {pf("phone", { label: "Phone Number", required: true }).visible && (
                      <div className="flex items-center gap-2 border-b border-slate-300">
                        <span className="text-[11px] font-bold text-slate-500 bg-slate-100 rounded px-2 py-1 shrink-0">IN +91</span>
                        <input required={!!pf("phone").required} placeholder={`${pf("phone").label || "Phone Number"}${pf("phone").required ? "*" : ""}`} value={pForm.phone} onChange={(e) => setPForm({ ...pForm, phone: e.target.value })} className="flex-1 py-2 text-sm focus:outline-none" data-testid="tpl-preview-phone" />
                      </div>
                      )}
                      {pf("experience", { label: "Total Work Experience (in years)", required: true }).visible && (
                      <select required={!!pf("experience").required} value={pForm.experience} onChange={(e) => setPForm({ ...pForm, experience: e.target.value })} className="w-full border-b border-slate-300 py-2 text-sm text-slate-700 focus:outline-none bg-white" data-testid="tpl-preview-experience">
                        <option value="">{pf("experience").label || "Total Work Experience (in years)"}{pf("experience").required ? "*" : ""}</option>
                        {["0-1", "1-3", "3-5", "5-10", "10+"].map((o) => <option key={o} value={o}>{o} years</option>)}
                      </select>
                      )}
                      <label className="flex items-start gap-2 pt-1 cursor-pointer">
                        <input type="checkbox" checked={pConsent} onChange={(e) => setPConsent(e.target.checked)} className="mt-0.5 w-4 h-4 accent-amber-500 shrink-0" data-testid="tpl-preview-consent" />
                        <span className="text-[10px] text-slate-500 leading-snug">By submitting this form, you consent to our Terms and Conditions &amp; Privacy Policy and to be contacted by us and our partners via Email / Call / WhatsApp / SMS.</span>
                      </label>
                      {pMsg && <p className="text-[11px] font-semibold text-red-600" data-testid="tpl-preview-error">{pMsg}</p>}
                      <button type="submit" disabled={pBusy} className="w-full rounded-lg py-2.5 text-sm font-extrabold text-[#0F1115] hover:opacity-90 transition-opacity disabled:opacity-60" style={{ background: "#F5B301" }} data-testid="tpl-preview-unlock-btn">
                        {pBusy ? "Unlocking…" : (pcfg.button_label || "UNLOCK PREVIEW")}
                      </button>
                    </form>
                  </div>
                )}
              </div>
              <div className="border-t lg:border-t-0 lg:border-l border-white/10">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 px-4 pt-4 pb-2">Up next</p>
                <div className="max-h-72 lg:max-h-none overflow-y-auto">
                  {(allPreviews.length ? allPreviews : [previewModal]).map((pv, k) => {
                    const active = pv.video_url === previewModal.video_url && pv.topic === previewModal.topic;
                    return (
                      <button key={k} onClick={() => { if (pv.video_url) { setPreviewModal(pv); setPMsg(""); } }}
                        className={`w-full flex items-center gap-3 px-4 py-3.5 text-left border-b border-white/5 last:border-0 ${active ? "bg-white/10" : "hover:bg-white/5"}`} data-testid={`tpl-preview-next-${k}`}>
                        <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-white" style={{ background: "#2563EB" }}>{playIcon("w-2.5 h-2.5 ml-px")}</span>
                        <span className="flex-1 min-w-0 text-xs font-bold text-white truncate">{pv.topic || "Preview"}<span className="block text-[10px] font-normal text-slate-400 truncate">{pv.module}</span></span>
                        {pv.duration && <span className="text-[11px] font-bold shrink-0" style={{ color: "#60A5FA" }}>{pv.duration}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function TProjects({ c }) {
  const projects = c.projects || [];
  const [start, setStart] = useState(0);
  const [expanded, setExpanded] = useState({});
  const VISIBLE = useVisibleCount();
  const TRUNC = 150;
  const maxStart = Math.max(0, projects.length - VISIBLE);
  useEffect(() => { setStart((s) => Math.min(s, maxStart)); }, [maxStart]);
  const visible = projects.slice(start, start + VISIBLE);
  return (
    <section className="max-w-7xl mx-auto px-6 py-14" data-testid="tpl-projects">
      <h2 className="text-3xl font-extrabold tracking-tight" style={{ color: NAVY }}>
        {c.title_black || c.title || "Industry Case Studies &"} {c.title_red && <span style={{ color: RED }}>{c.title_red}</span>}
      </h2>
      {c.subtitle && <p className="text-base text-slate-500 mt-3 max-w-2xl leading-relaxed">{c.subtitle}</p>}
      <div className="relative mt-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.map((p, i) => {
            const idx = start + i;
            const text = p.text || "";
            const isLong = text.length > TRUNC;
            const isOpen = !!expanded[idx];
            return (
              <div key={idx} data-testid={`tpl-project-${idx}`}
                className={`rounded-2xl bg-white shadow-sm p-8 transition-shadow hover:shadow-md ${i === 0 ? "border-2" : "border border-slate-200"}`}
                style={i === 0 ? { borderColor: RED } : {}}>
                {p.image
                  ? <img src={p.image} alt={p.image_alt || p.title || p.logo_text || "Project"} title={p.image_title || undefined} className="h-12 w-auto object-contain mb-6" />
                  : p.logo_text
                    ? <p className="text-3xl font-extrabold mb-6 tracking-tight" style={{ color: p.logo_color || "#111827" }}>{p.logo_text}</p>
                    : <span className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{ background: "#FEF6E0" }}><FolderKanban className="w-6 h-6" style={{ color: RED }} /></span>}
                <p className="text-lg font-extrabold text-slate-900">{p.title}</p>
                {text ? (
                  <p className="text-sm text-slate-600 leading-relaxed mt-3">
                    {isOpen || !isLong ? text : text.slice(0, TRUNC).trimEnd() + "…"}
                    {isLong && (
                      <button onClick={() => setExpanded({ ...expanded, [idx]: !isOpen })}
                        className="font-bold underline ml-1 text-slate-900" data-testid={`tpl-project-readmore-${idx}`}>
                        {isOpen ? "Read less" : "Read more"}
                      </button>
                    )}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {(p.tags || []).map((t, j) => <span key={j} className="text-[10px] font-semibold text-slate-500 bg-slate-50 border border-slate-100 rounded px-2 py-1">{t}</span>)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {start > 0 && (
          <button onClick={() => setStart(Math.max(0, start - 1))} data-testid="tpl-projects-prev"
            className="absolute left-1 lg:-left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        {start < maxStart && (
          <button onClick={() => setStart(Math.min(maxStart, start + 1))} data-testid="tpl-projects-next"
            className="absolute right-1 lg:-right-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </section>
  );
}

function TFaculty({ c }) {
  const members = c.members || [];
  const [start, setStart] = useState(0);
  const [expanded, setExpanded] = useState({});
  const VISIBLE = useVisibleCount();
  const TRUNC = 140;
  const maxStart = Math.max(0, members.length - VISIBLE);
  useEffect(() => { setStart((s) => Math.min(s, maxStart)); }, [maxStart]);
  const visible = members.slice(start, start + VISIBLE);
  return (
    <section className="max-w-7xl mx-auto px-6 py-14" data-testid="tpl-faculty">
      <h2 className="text-3xl font-extrabold tracking-tight" style={{ color: NAVY }}>
        {c.title_black || c.title || "Mentored by"} {c.title_red && <span style={{ color: RED }}>{c.title_red}</span>}
      </h2>
      <div className="mt-6 border-b border-slate-200">
        <span className="inline-block text-base font-extrabold pb-2 border-b-2 text-slate-900" style={{ borderColor: RED }}>{c.tab_label || "Faculty Members"}</span>
      </div>
      <div className="relative mt-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.map((m, i) => {
            const idx = start + i;
            const bio = m.bio || m.exp || "";
            const isLong = bio.length > TRUNC;
            const isOpen = !!expanded[idx];
            return (
              <div key={idx} className="rounded-2xl border border-slate-200 bg-white shadow-sm p-7 hover:shadow-md transition-shadow" data-testid={`tpl-faculty-${idx}`}>
                <div className="flex items-center gap-4">
                  <span className="w-14 h-14 rounded-full overflow-hidden bg-slate-100 shrink-0 border border-slate-200">
                    {m.image && <img src={m.image} alt={m.image_alt || m.name} title={m.image_title || undefined} className="w-full h-full object-cover object-top" />}
                  </span>
                  <div>
                    <p className="text-lg font-extrabold text-slate-900">{m.name}</p>
                    {m.company && <p className="text-base font-extrabold mt-0.5" style={{ color: RED }}>{m.company}</p>}
                  </div>
                </div>
                <div className="border-t border-slate-100 my-5" />
                <p className="flex items-center gap-2.5 text-sm font-bold text-slate-900">
                  <UserCheck className="w-5 h-5 shrink-0" style={{ color: RED }} /> {m.domain || m.role}
                </p>
                {bio && (
                  <p className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed mt-4">
                    <Star className="w-5 h-5 shrink-0 mt-0.5" style={{ color: RED }} />
                    <span>
                      {isOpen || !isLong ? bio : bio.slice(0, TRUNC).trimEnd() + "…"}
                      {isLong && (
                        <button onClick={() => setExpanded({ ...expanded, [idx]: !isOpen })}
                          className="font-bold underline ml-1 text-slate-900" data-testid={`tpl-faculty-readmore-${idx}`}>
                          {isOpen ? "Read Less" : "Read More"}
                        </button>
                      )}
                    </span>
                  </p>
                )}
                {m.linkedin && (
                  <a href={m.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center mt-6" title="LinkedIn profile" data-testid={`tpl-faculty-linkedin-${idx}`}>
                    <Linkedin className="w-4 h-4" style={{ color: "#0A66C2" }} />
                  </a>
                )}
              </div>
            );
          })}
        </div>
        {start > 0 && (
          <button onClick={() => setStart(Math.max(0, start - 1))} data-testid="tpl-faculty-prev"
            className="absolute left-1 lg:-left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        {start < maxStart && (
          <button onClick={() => setStart(Math.min(maxStart, start + 1))} data-testid="tpl-faculty-next"
            className="absolute right-1 lg:-right-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </section>
  );
}

function TTestimonials({ c }) {
  const items = c.testimonials || [];
  const [start, setStart] = useState(0);
  const [expanded, setExpanded] = useState({});
  const VISIBLE = useVisibleCount();
  const TRUNC = 170;
  const maxStart = Math.max(0, items.length - VISIBLE);
  useEffect(() => { setStart((s) => Math.min(s, maxStart)); }, [maxStart]);
  const visible = items.slice(start, start + VISIBLE);
  return (
    <section className="max-w-7xl mx-auto px-6 py-14" data-testid="tpl-testimonials">
      <h2 className="text-3xl font-extrabold tracking-tight" style={{ color: NAVY }}>
        {c.title_black || "What"} <span style={{ color: RED }}>{c.title_red || "Our Learners Have to Say"}</span>
      </h2>
      <div className="relative mt-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.map((t, i) => {
            const idx = start + i;
            const text = t.text || "";
            const isLong = text.length > TRUNC;
            const isOpen = !!expanded[idx];
            return (
              <div key={idx} className="flex flex-col" data-testid={`tpl-testimonial-${idx}`}>
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-7 hover:shadow-md transition-shadow">
                  <Quote className="w-7 h-7 fill-current" style={{ color: RED }} />
                  <p className="text-base font-extrabold mt-4 text-slate-900">{t.role}</p>
                  <p className="text-sm text-slate-600 leading-relaxed mt-4">
                    {isOpen || !isLong ? text : text.slice(0, TRUNC).trimEnd() + "…"}
                    {isLong && (
                      <button onClick={() => setExpanded({ ...expanded, [idx]: !isOpen })}
                        className="font-bold underline ml-1 text-slate-900"
                        data-testid={`tpl-testimonial-readmore-${idx}`}>
                        {isOpen ? "Read Less" : "Read More"}
                      </button>
                    )}
                  </p>
                  {t.linkedin && (
                    <a href={t.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center mt-5" title="LinkedIn profile" data-testid={`tpl-testimonial-linkedin-${idx}`}>
                      <Linkedin className="w-4 h-4" style={{ color: "#0A66C2" }} />
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-auto pt-6 pl-2">
                  <span className="w-16 h-16 rounded-full overflow-hidden bg-slate-100 shrink-0 border border-slate-200">
                    {t.image && <img src={t.image} alt={t.image_alt || t.name} title={t.image_title || undefined} className="w-full h-full object-cover" />}
                  </span>
                  <div>
                    <p className="text-base font-extrabold" style={{ color: NAVY }}>{t.name}</p>
                    <p className="text-sm text-slate-500 mt-0.5">{t.ctc}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {start > 0 && (
          <button onClick={() => setStart(Math.max(0, start - 1))} data-testid="tpl-testimonials-prev"
            className="absolute left-1 lg:-left-5 top-24 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        {start < maxStart && (
          <button onClick={() => setStart(Math.min(maxStart, start + 1))} data-testid="tpl-testimonials-next"
            className="absolute right-1 lg:-right-5 top-24 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </section>
  );
}

const GROWTH_THEMES = {
  yellow: { bg: "linear-gradient(120deg,#FFD84D 0%,#FFC72C 100%)", text: "#17263B", sub: "#4A5568", chipBg: "rgba(23,38,59,0.08)", btnBg: "#17263B", btnText: "#FFFFFF", statBorder: "rgba(23,38,59,0.25)" },
  blue: { bg: "linear-gradient(120deg,#1D4ED8 0%,#1E3FAE 100%)", text: "#FFFFFF", sub: "#C9D8F8", chipBg: "rgba(255,255,255,0.18)", btnBg: "#FFFFFF", btnText: "#1E3FAE", statBorder: "rgba(255,255,255,0.35)" },
  green: { bg: "linear-gradient(120deg,#0E7C3A 0%,#0A5E2D 100%)", text: "#FFFFFF", sub: "#C9EAD4", chipBg: "rgba(255,255,255,0.18)", btnBg: "#D9F7E3", btnText: "#0A5E2D", statBorder: "rgba(255,255,255,0.35)" },
  red: { bg: "linear-gradient(120deg,#C0272D 0%,#8F1A20 100%)", text: "#FFFFFF", sub: "#F6CFD2", chipBg: "rgba(255,255,255,0.18)", btnBg: "#FBD3E0", btnText: "#8F1A20", statBorder: "rgba(255,255,255,0.35)" },
};

function GrowthCard({ icon, title, items, cls, delay }) {
  const list = (items || "").split(",").map((s) => s.trim()).filter(Boolean);
  if (!title && !list.length) return null;
  const check = (
    <svg viewBox="0 0 12 12" className="w-3 h-3 text-emerald-500 shrink-0" aria-hidden="true"><path d="M2 6.5L4.8 9L10 3.5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" /></svg>
  );
  return (
    <div className={`absolute bg-white rounded-2xl shadow-lg px-4 py-3.5 w-40 sm:w-52 tpl-growth-float ${cls || ""}`} style={{ animationDelay: delay || "0s" }}>
      {title && <p className="text-xs font-extrabold text-slate-900 mb-2">{title}</p>}
      {icon === "users" && (
        <div className="flex items-center">
          {["AK", "RS", "PM"].map((t, i) => (
            <span key={i} className="w-7 h-7 rounded-full bg-slate-900 text-white text-[9px] font-extrabold flex items-center justify-center border-2 border-white -ml-1.5 first:ml-0">{t}</span>
          ))}
          <span className="w-7 h-7 rounded-full bg-slate-100 text-slate-500 text-[10px] font-extrabold flex items-center justify-center border-2 border-white -ml-1.5">+</span>
        </div>
      )}
      {icon === "chart" && (list.length ? (
        <div className="flex flex-col gap-2">
          {list.map((it, i) => {
            const [label, pct] = it.split("|").map((s) => s.trim());
            return (
              <div key={i}>
                <p className="text-[10px] font-semibold text-slate-600 mb-0.5">{label}</p>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.min(Number(pct) || 70, 100)}%` }} /></div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full w-3/4 rounded-full" style={{ background: "#F5B301" }} /></div>
      ))}
      {icon === "target" && (
        <div className="flex flex-col gap-1.5">
          {list.map((it, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: ["#F5B301", "#FB923C", "#DC2626", "#991B1B"][i % 4] }} />
              <span className="text-[11px] font-semibold text-slate-600">{it}</span>
            </div>
          ))}
        </div>
      )}
      {icon !== "users" && icon !== "chart" && icon !== "target" && list.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {list.map((it, i) => (
            <div key={i} className="flex items-center gap-1.5">
              {check}
              <span className="text-[11px] font-semibold text-slate-600">{it.split("|")[0].trim()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GrowthPanel({ p, i, onApply }) {
  const t = GROWTH_THEMES[p.theme] || GROWTH_THEMES.yellow;
  const btnCls = "inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-extrabold hover:opacity-90 transition-opacity mt-7";
  return (
    <div className="sticky mb-6 last:mb-0" style={{ top: `${84 + i * 22}px`, zIndex: i + 1 }} data-testid={`tpl-growth-panel-${i}`}>
      <div className="rounded-3xl shadow-xl overflow-hidden" style={{ background: t.bg }}>
        <div className="grid md:grid-cols-2 items-center gap-6 sm:gap-10 p-7 sm:p-12">
          <div>
            {p.badge && <span className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-extrabold" style={{ background: t.chipBg, color: t.text }}>{p.badge}</span>}
            <h3 className="text-2xl sm:text-4xl font-extrabold tracking-tight leading-tight mt-4" style={{ color: t.text }}>{p.title}</h3>
            {p.subtitle && <p className="text-sm sm:text-base mt-3 leading-relaxed" style={{ color: t.sub }}>{p.subtitle}</p>}
            <div className="flex items-center gap-8 mt-6">
              <div>
                <p className="text-2xl sm:text-3xl font-extrabold" style={{ color: t.text }}>{p.stat1_value}</p>
                <p className="text-xs mt-0.5" style={{ color: t.sub }}>{p.stat1_label}</p>
              </div>
              <span className="w-px h-10" style={{ background: t.statBorder }} />
              <div>
                <p className="text-2xl sm:text-3xl font-extrabold" style={{ color: t.text }}>{p.stat2_value}</p>
                <p className="text-xs mt-0.5" style={{ color: t.sub }}>{p.stat2_label}</p>
              </div>
            </div>
            {p.cta_link ? (
              <a href={p.cta_link} className={btnCls} style={{ background: t.btnBg, color: t.btnText }} data-testid={`tpl-growth-cta-${i}`}>{p.cta_label || "Learn more"} <span aria-hidden="true">&rarr;</span></a>
            ) : (
              <button onClick={onApply} className={btnCls} style={{ background: t.btnBg, color: t.btnText }} data-testid={`tpl-growth-cta-${i}`}>{p.cta_label || "Apply Now"} <span aria-hidden="true">&rarr;</span></button>
            )}
          </div>
          <div className="relative mt-2 md:mt-0 pb-10 md:pb-0">
            {p.image && <img src={p.image} alt={p.title || ""} className="w-full h-52 sm:h-72 object-cover rounded-2xl" loading="lazy" data-testid={`tpl-growth-img-${i}`} />}
            <GrowthCard icon={p.card_a_icon} title={p.card_a_title} items={p.card_a_items} cls="right-1 -top-5 sm:-right-3 sm:-top-6" delay="0s" />
            <GrowthCard icon={p.card_b_icon} title={p.card_b_title} items={p.card_b_items} cls="left-1 -bottom-1 sm:-left-4 sm:bottom-6" delay="1.4s" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function TGrowth({ c, onApply }) {
  const panels = c.panels || [];
  if (!panels.length) return null;
  return (
    <section className="py-14 bg-white" data-testid="tpl-growth">
      <style>{`@keyframes tpl-growth-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-7px); } } .tpl-growth-float { animation: tpl-growth-float 4.5s ease-in-out infinite; } @media (prefers-reduced-motion: reduce) { .tpl-growth-float { animation: none; } }`}</style>
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-extrabold tracking-tight" style={{ color: NAVY }}>
            {c.title_black || "From Learning to"} <span style={{ color: RED }}>{c.title_red || "Career Growth"}</span>
          </h2>
          {c.subtitle && <p className="text-base text-slate-500 mt-2">{c.subtitle}</p>}
        </div>
        <div className="relative mt-10">
          <svg className="absolute -left-3 top-8 bottom-8 w-12 h-full hidden lg:block" viewBox="0 0 48 1200" fill="none" preserveAspectRatio="none" aria-hidden="true">
            <path d="M38 0 C 58 160, 4 320, 24 480 C 44 640, 8 800, 30 960 C 44 1060, 18 1140, 34 1200" stroke="#E2E8F0" strokeWidth="2.5" strokeDasharray="7 9" fill="none" />
          </svg>
          {panels.map((p, i) => <GrowthPanel key={i} p={p} i={i} onApply={onApply} />)}
        </div>
      </div>
    </section>
  );
}

function TEmployers({ c }) {
  const logos = c.logos || [];
  if (!logos.length) return null;
  const half = Math.ceil(logos.length / 2);
  const fill = (arr) => { let a = [...arr]; while (a.length && a.length < 6) a = [...a, ...arr]; return a.length ? a : logos; };
  const rowA = fill(logos.slice(0, half));
  const rowB = fill(logos.slice(half));
  const Card = ({ l, i, row }) => (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-4 shadow-sm shrink-0" data-testid={`tpl-employer-${row}-${i}`}>
      {l.image ? (
        <img src={l.image} alt={l.image_alt || l.name || "Employer logo"} title={l.image_title || l.name || ""} className="h-8 w-auto max-w-[110px] object-contain" />
      ) : (
        <span className="w-9 h-9 rounded-xl flex items-center justify-center text-[11px] font-extrabold text-white shrink-0" style={{ background: l.color || NAVY }}>
          {(l.name || "CO").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
        </span>
      )}
      <span className="text-sm font-extrabold whitespace-nowrap" style={{ color: NAVY }}>{l.name}</span>
    </div>
  );
  return (
    <section className="bg-slate-50 border-y border-slate-100 py-14 overflow-hidden" data-testid="tpl-employers">
      <style>{`@keyframes tpl-emp-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } } .tpl-emp-marquee { animation: tpl-emp-marquee 36s linear infinite; } .tpl-emp-marquee:hover { animation-play-state: paused; } .tpl-emp-rev { animation-direction: reverse; } @media (prefers-reduced-motion: reduce) { .tpl-emp-marquee { animation: none; } }`}</style>
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-3xl font-extrabold tracking-tight" style={{ color: NAVY }}>
          {c.title_black || "Where Our"} <span style={{ color: RED }}>{c.title_red || "Learners Work"}</span>
        </h2>
        {c.subtitle && <p className="text-base text-slate-500 mt-2">{c.subtitle}</p>}
      </div>
      <div className="mt-10 flex flex-col gap-4">
        {[{ row: rowA, rev: false, key: "a" }, { row: rowB, rev: true, key: "b" }].map(({ row, rev, key }) => (
          <div key={key} className="overflow-hidden py-1" style={{ maskImage: "linear-gradient(90deg, transparent, black 8%, black 92%, transparent)" }} data-testid={`tpl-employers-row-${key}`}>
            <div className={`flex items-center gap-4 w-max tpl-emp-marquee ${rev ? "tpl-emp-rev" : ""}`}>
              {[...row, ...row].map((l, i) => <Card key={i} l={l} i={i} row={key} />)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TJourney({ c }) {
  return (
    <section className="max-w-7xl mx-auto px-6 py-12">
      <SectionHead title={c.title} subtitle={c.subtitle} />
      <div className="flex flex-col sm:flex-row items-start gap-4">
        {(c.steps || []).map((s, i) => (
          <div key={i} className="flex items-center gap-4 flex-1 w-full">
            <div className="flex-1">
              <span className="inline-block text-xs font-extrabold text-white rounded-lg px-3 py-1.5 mb-2" style={{ background: BLUE }}>{s.num}</span>
              <p className="text-sm font-extrabold" style={{ color: NAVY }}>{s.title}</p>
              <p className="text-[11px] text-slate-400 font-semibold">{s.months}</p>
            </div>
            {i < (c.steps || []).length - 1 && <ArrowRight className="w-5 h-5 text-slate-300 shrink-0 hidden sm:block" />}
          </div>
        ))}
      </div>
    </section>
  );
}

const SplitTitle = ({ text, center = false }) => {
  const words = (text || "").trim().split(" ");
  const last = words.pop() || "";
  return (
    <h2 className={`text-2xl sm:text-3xl font-extrabold tracking-tight ${center ? "text-center" : ""}`} style={{ color: NAVY }}>
      {words.join(" ")} <span style={{ color: RED }}>{last}</span>
    </h2>
  );
};

function TAdmission({ c, onApply }) {
  const steps = c.steps || [];
  const [start, setStart] = useState(0);
  const VISIBLE = useVisibleCount();
  const maxStart = Math.max(0, steps.length - VISIBLE);
  useEffect(() => { setStart((s) => Math.min(s, maxStart)); }, [maxStart]);
  const visible = steps.slice(start, start + VISIBLE);
  const secRef = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = secRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <section ref={secRef} className="max-w-7xl mx-auto px-6 py-14" data-testid="tpl-admission">
      <h2 className="text-3xl font-extrabold tracking-tight">
        <span style={{ color: RED }}>{c.title_red || "Admission"}</span>{" "}
        <span style={{ color: NAVY }}>{c.title_black || "Process"}</span>
      </h2>
      {c.subtitle && <p className="text-base text-slate-500 mt-3 max-w-2xl leading-relaxed">{c.subtitle}</p>}
      <div className="flex items-center gap-4 mt-10">
        <span className="w-14 h-14 rounded-full flex items-center justify-center shrink-0" style={{ background: "#FEF6E0" }}>
          <BriefcaseBusiness className="w-6 h-6" style={{ color: RED }} />
        </span>
        <div>
          <p className="text-lg font-bold text-slate-600">{c.intro_label || "Start Your Application"}</p>
          <p className="text-lg font-extrabold text-slate-900 mt-0.5">{c.intro_text || "Follow the below application process to begin your enrollment process."}</p>
        </div>
      </div>
      <div className="relative mt-12">
        <div className="flex flex-col lg:flex-row items-stretch gap-10 lg:gap-0">
          {visible.map((s, i) => {
            const idx = start + i;
            return (
              <div key={idx} className="flex-1 flex items-stretch" data-testid={`tpl-admission-step-${idx}`}
                style={{ opacity: inView ? 1 : 0, transform: inView ? "translateY(0)" : "translateY(24px)", transition: `opacity 0.5s ease ${i * 0.18}s, transform 0.5s ease ${i * 0.18}s` }}>
                <div className="flex-1 px-2 lg:px-6">
                  <div className="h-28 flex items-end justify-center">
                    {s.image && <img src={s.image} alt={s.image_alt || s.title} title={s.image_title || undefined} className="h-28 w-auto object-contain" />}
                  </div>
                  <p className="text-xl font-extrabold text-slate-900 mt-6 leading-snug">{s.title}</p>
                  <p className="text-sm text-slate-500 leading-relaxed mt-3">{s.text}</p>
                </div>
                {i < visible.length - 1 && (
                  <div className="hidden lg:flex items-center shrink-0 pt-4">
                    <span className="w-12 border-t-2 border-dashed border-slate-300" />
                    <ChevronRight className="w-4 h-4 -ml-1 text-slate-400" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {start > 0 && (
          <button onClick={() => setStart(Math.max(0, start - 1))} data-testid="tpl-admission-prev"
            className="absolute left-1 lg:-left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        {start < maxStart && (
          <button onClick={() => setStart(Math.min(maxStart, start + 1))} data-testid="tpl-admission-next"
            className="absolute right-1 lg:-right-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>
      {c.cta_label && (
        <div className="mt-10">
          <button onClick={() => onApply?.()} data-testid="tpl-admission-cta"
            className="inline-flex items-center gap-2 px-7 h-12 rounded-xl font-bold text-white shadow-lg hover:opacity-90 transition-opacity"
            style={{ background: RED }}>
            {c.cta_label} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </section>
  );
}

function TFee({ c, onApply, onBrochure }) {
  const feeImage = c.image || "https://static.prod-images.emergentagent.com/jobs/fce244d9-4f06-4792-baea-c2f10101634a/images/881f41a46f0849ffc8247dc650483db7621caa3dea0d4f5a9388993f44bb6789.jpeg";
  return (
    <div>
      <section className="max-w-7xl mx-auto px-6 pt-14" data-testid="tpl-fee-block">
        <h2 className="text-3xl font-extrabold tracking-tight" style={{ color: NAVY }}>
          {c.title_black || "Program"} <span style={{ color: RED }}>{c.title_red || "Course Fee"}</span>
        </h2>
        <div className="mt-6 border-b border-slate-200">
          <span className="inline-block text-sm font-bold pb-2 border-b-2" style={{ color: NAVY, borderColor: RED }}>{c.tab_label || "Course Fee"}</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mt-10">
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-8 max-w-lg">
            {c.duration_pill && <span className="inline-block text-[11px] font-extrabold tracking-wide border border-slate-200 rounded-full px-4 py-1.5 text-slate-700">{c.duration_pill}</span>}
            <p className="text-xl font-bold mt-6" style={{ color: NAVY }}>{c.starting_label || "Starting at"}</p>
            <p className="text-3xl font-extrabold mt-1" style={{ color: RED }}>{c.emi_price || c.emi}</p>
            <p className="text-sm font-bold mt-1" style={{ color: NAVY }}>{c.total_price || c.price}</p>
            <div className="border-t border-slate-100 my-6" />
            <p className="text-sm font-bold" style={{ color: NAVY }}>{c.reserve_label || "Reserve your seat"}</p>
            <p className="flex items-center gap-2 text-sm text-slate-600 mt-2"><Check className="w-4 h-4 shrink-0" style={{ color: RED }} />{c.seat_note}</p>
            <button onClick={onApply} data-testid="tpl-fee-apply"
              className="w-full mt-7 py-3.5 rounded-xl font-bold text-sm text-white transition-opacity hover:opacity-90" style={{ background: RED }}>
              {c.cta_primary || "Apply Now"}
            </button>
            {c.plans_link_label && (
              <a href={c.plans_link_url || "#"} className="block text-center text-xs font-bold underline mt-4 hover:opacity-70" style={{ color: NAVY }} data-testid="tpl-fee-plans">{c.plans_link_label}</a>
            )}
            {c.cta_secondary && (
              <button onClick={onBrochure} className="block mx-auto text-center text-xs font-bold underline mt-3 text-slate-500 hover:text-slate-700" data-testid="tpl-fee-brochure">{c.cta_secondary}</button>
            )}
          </div>
          <div className="hidden lg:block">
            <img src={feeImage} alt={c.image_alt || "Program fee"} title={c.image_title || undefined} className="w-full max-w-lg mx-auto rounded-2xl" />
          </div>
        </div>
      </section>
      <section className="bg-slate-50/70 border-y border-slate-100 mt-14" data-testid="tpl-included-block">
        <div className="max-w-7xl mx-auto px-6 py-12 sm:py-14">
          <span className="block w-10 h-1 rounded-full" style={{ background: "#F5B301" }} />
          <div className="mt-3"><SplitTitle text={c.included_title || "What's Included?"} /></div>
          {c.included_subtitle && <p className="text-sm sm:text-base text-slate-500 mt-2.5 max-w-2xl" data-testid="tpl-included-subtitle">{c.included_subtitle}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {(c.included || []).map((x) => (typeof x === "string" ? (HIGHLIGHT_ICONS[x] ? { icon: x, title: "" } : { title: x }) : x)).filter((x) => !x.hidden).map((it, i) => {
              const Icon = HIGHLIGHT_ICONS[it.icon] || INCLUDED_ICONS[i % INCLUDED_ICONS.length];
              const tone = TONE_STYLES[it.tone] || TONE_STYLES[TONE_CYCLE[i % TONE_CYCLE.length]];
              return (
                <div key={i} className="flex items-center sm:items-start gap-3.5 rounded-2xl border border-slate-200 bg-white px-4 py-3.5 sm:p-5" data-testid={`tpl-included-${i}`}>
                  <span className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: tone.bg }}>
                    <Icon className="w-5 h-5" style={{ color: tone.fg }} />
                  </span>
                  <div className="min-w-0">
                    {it.title && <p className="text-sm font-bold leading-snug" style={{ color: NAVY }}>{it.title}</p>}
                    {it.desc && <p className="text-xs text-slate-500 mt-1 leading-relaxed">{it.desc}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-6 py-12 sm:py-14" data-testid="tpl-audience-block">
        <span className="block w-10 h-1 rounded-full" style={{ background: "#F5B301" }} />
        <div className="mt-3"><SplitTitle text={c.audience_title || "Who Should Join?"} /></div>
        {c.audience_subtitle && <p className="text-sm sm:text-base text-slate-500 mt-2.5 max-w-2xl" data-testid="tpl-audience-subtitle">{c.audience_subtitle}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {(c.audience_icons || []).map((x) => (typeof x === "string" ? (HIGHLIGHT_ICONS[x] ? { icon: x, title: "" } : { title: x }) : x)).filter((x) => !x.hidden).map((a, i) => {
            const Icon = HIGHLIGHT_ICONS[a.icon] || AUDIENCE_ICONS[i % AUDIENCE_ICONS.length];
            const tone = TONE_STYLES[a.tone] || TONE_STYLES[TONE_CYCLE[i % TONE_CYCLE.length]];
            return (
              <div key={i} className="flex sm:flex-col items-center sm:items-start gap-3.5 rounded-2xl border border-slate-200 bg-white px-4 py-3.5 sm:p-5" data-testid={`tpl-audience-${i}`}>
                <span className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: tone.bg }}>
                  <Icon className="w-5 h-5" style={{ color: tone.fg }} />
                </span>
                <div className="min-w-0">
                  {a.title && <p className="text-sm font-extrabold leading-snug" style={{ color: NAVY }}>{a.title}</p>}
                  {a.desc && <p className="text-xs text-slate-500 mt-1 leading-relaxed">{a.desc}</p>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
          {(c.audience || []).map((a, i) => (
            <p key={i} className="flex items-center gap-2.5 text-sm text-slate-600 rounded-xl bg-white border border-slate-200 px-4 py-3" data-testid={`tpl-audience-item-${i}`}>
              <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ background: "#FEF3C7" }}>
                <Check className="w-3 h-3" style={{ color: "#D97706" }} />
              </span>
              {a}
            </p>
          ))}
        </div>
      </section>
    </div>
  );
}

const COMPARE_ICONS = { tool: Wrench, genai: Network, projects: FolderKanban, cases: BookOpen, deploy: Rocket, portfolio: IdCard, interview: UserCheck, placement: BriefcaseBusiness };

function TCompare({ c }) {
  const rows = c.rows || [];
  return (
    <section className="max-w-7xl mx-auto px-6 py-14" data-testid="tpl-compare">
      <h2 className="text-3xl font-extrabold tracking-tight" style={{ color: NAVY }}>
        {c.title_black || "What Makes This Program"} <span style={{ color: RED }}>{c.title_red || "Stand Out?"}</span>
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.25fr_1.1fr] mt-10">
        <div className="hidden lg:block" />
        <div className="border-x-2 border-t-2 rounded-t-3xl px-6 pt-6 pb-3 bg-white" style={{ borderColor: RED }}>
          <p className="text-xl font-extrabold leading-snug" style={{ color: RED }}>{c.col_a_title || "Our Program"}</p>
        </div>
        <div className="px-6 pt-6 pb-3 flex items-end">
          <p className="text-lg font-extrabold" style={{ color: NAVY }}>{c.col_b_title || "Other Programs"}</p>
        </div>
        {rows.map((r, i) => {
          const Icon = COMPARE_ICONS[r.icon] || Wrench;
          const last = i === rows.length - 1;
          return (
            <div key={i} className="contents" data-testid={`tpl-compare-row-${i}`}>
              <div className="flex items-center gap-3 px-2 lg:px-0 py-5 border-b border-slate-100">
                <Icon className="w-6 h-6 shrink-0 text-slate-800" />
                <span className="text-sm font-extrabold text-slate-900">{r.feature}</span>
              </div>
              <div className={`flex items-start gap-2.5 px-6 py-5 bg-white border-x-2 ${last ? "border-b-2 rounded-b-3xl pb-7" : ""}`} style={{ borderColor: RED }}>
                <ThumbsUp className="w-5 h-5 shrink-0 mt-0.5 text-emerald-500" />
                <span className="text-sm font-bold text-slate-800">{r.a_text}</span>
              </div>
              <div className="flex items-start gap-2.5 px-6 py-5 border-b border-slate-100">
                <ThumbsDown className="w-5 h-5 shrink-0 mt-0.5 text-slate-300" />
                <span className="text-sm text-slate-400">{r.b_text}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function TAbout({ c }) {
  const [expanded, setExpanded] = useState(false);
  const paras = c.paragraphs || [];
  const hasMore = paras.length > 1 || c.why_title || c.why_text || (c.rows || []).length > 0;
  return (
    <section className="max-w-7xl mx-auto px-6 py-14" data-testid="tpl-about">
      <div className="rounded-3xl border border-slate-200 bg-white shadow-sm px-8 sm:px-12 py-12 max-w-5xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
          <span className="text-slate-900">{c.title_black || "Gen AI-powered"}</span>{" "}
          <span style={{ color: RED }}>{c.title_red || "Data Science with Machine Learning"}</span>
        </h2>
        <div className="flex flex-col gap-5 mt-6">
          {(expanded ? paras : paras.slice(0, 1)).map((p, i) => (
            <p key={i} className="text-base text-slate-800 leading-relaxed">{p}</p>
          ))}
        </div>
        {expanded && c.why_title && <p className="text-lg font-extrabold text-slate-900 mt-10">{c.why_title}</p>}
        {expanded && c.why_text && <p className="text-base text-slate-800 leading-relaxed mt-3">{c.why_text}</p>}
        {expanded && (c.rows || []).length > 0 && (
          <div className="overflow-x-auto mt-6">
            <table className="w-full border border-slate-300 text-base" data-testid="tpl-about-table">
              <thead>
                <tr>
                  <th className="border border-slate-300 px-5 py-3.5 text-center font-extrabold text-slate-900">{c.header_a || "Program Highlights"}</th>
                  <th className="border border-slate-300 px-5 py-3.5 text-center font-extrabold text-slate-900">{c.header_b || "Details"}</th>
                </tr>
              </thead>
              <tbody>
                {c.rows.map((r, i) => (
                  <tr key={i} data-testid={`tpl-about-row-${i}`}>
                    <td className="border border-slate-300 px-5 py-3.5 text-slate-800">{r.label}</td>
                    <td className="border border-slate-300 px-5 py-3.5 text-slate-800">{r.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {hasMore && (
          <div className="flex justify-end mt-6">
            <button onClick={() => setExpanded(!expanded)} className="text-sm font-bold underline hover:opacity-80" style={{ color: RED }} data-testid="tpl-about-readmore">
              {expanded ? "Read Less" : "Read More"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function TFaq({ c }) {
  const cats = c.categories || [];
  const [cat, setCat] = useState(0);
  const [open, setOpen] = useState(0);
  const faqs = cats[cat]?.faqs || [];
  return (
    <section className="max-w-7xl mx-auto px-6 py-14" data-testid="tpl-faq">
      <h2 className="text-3xl font-extrabold tracking-tight" style={{ color: NAVY }}>
        {c.title_black || "Frequently Asked"} <span style={{ color: RED }}>{c.title_red || "Questions"}</span>
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-10 mt-10">
        <div className="flex flex-col gap-4">
          {cats.map((ct, i) => (
            <button key={i} onClick={() => { setCat(i); setOpen(0); }}
              className={`text-left text-base font-bold rounded-2xl border px-6 py-4 transition-all ${cat === i ? "shadow-md" : "text-slate-400 hover:text-slate-600"}`}
              style={{ borderColor: "#e2e8f0", color: cat === i ? RED : undefined }}
              data-testid={`tpl-faq-cat-${i}`}>{ct.name}</button>
          ))}
        </div>
        <div className="flex flex-col">
          {faqs.map((f, i) => (
            <div key={i} className={`py-6 ${i < faqs.length - 1 ? "border-b border-slate-100" : ""}`} data-testid={`tpl-faq-item-${i}`}>
              <button className="w-full flex items-start justify-between gap-4 text-left" onClick={() => setOpen(open === i ? -1 : i)} data-testid={`tpl-faq-q-${i}`}>
                <span className="text-base sm:text-lg font-bold" style={{ color: open === i ? RED : "#111827" }}>{f.q}</span>
                {open === i
                  ? <Minus className="w-5 h-5 shrink-0 mt-1" style={{ color: RED }} />
                  : <Plus className="w-5 h-5 shrink-0 mt-1 text-slate-900" />}
              </button>
              {open === i && <p className="text-sm sm:text-base text-slate-600 leading-relaxed mt-3 max-w-3xl">{f.a}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TSupport({ c }) {
  return (
    <section className="max-w-7xl mx-auto px-6 py-14" data-testid="tpl-support">
      <h2 className="text-3xl font-extrabold tracking-tight" style={{ color: NAVY }}>
        {c.brand_black || "Training Basket"} <span style={{ color: RED }}>{c.brand_red || "Learner Support"}</span>
      </h2>
      <p className="text-sm sm:text-base text-slate-500 mt-2">{c.subtitle}</p>
      <div className="flex flex-wrap items-center gap-6 mt-8">
        <span className="flex items-center gap-2.5">
          <span className="w-6 h-6 rounded-full overflow-hidden inline-block" style={{ background: "linear-gradient(180deg,#FF9933 0 33%,#FFFFFF 33% 66%,#138808 66% 100%)", border: "1px solid #e2e8f0" }} />
          <span className="text-sm font-extrabold" style={{ color: NAVY }}>{c.flag_label}</span>
        </span>
        <a href={`tel:${(c.phone || "").replace(/\s/g, "")}`} data-testid="tpl-support-phone"
          className="flex items-center gap-2.5 rounded-xl border px-6 py-3 text-lg font-extrabold transition-colors hover:bg-red-50"
          style={{ borderColor: RED, color: "#111827" }}>
          <Phone className="w-5 h-5" style={{ color: RED }} /> {c.phone}
        </a>
      </div>
    </section>
  );
}

function TDisclaimer({ c }) {
  const [expanded, setExpanded] = useState(false);
  const paras = c.paragraphs || [];
  const visible = expanded ? paras : paras.slice(0, 1);
  return (
    <section className="max-w-7xl mx-auto px-6 py-14" data-testid="tpl-disclaimer">
      <h2 className="text-2xl font-extrabold tracking-tight mb-5" style={{ color: NAVY }}>{c.title || "Disclaimer"}</h2>
      <div className="flex flex-col gap-3">
        {visible.map((p, i) => <p key={i} className="text-sm text-slate-600 leading-relaxed">{p}</p>)}
      </div>
      {paras.length > 1 && (
        <button onClick={() => setExpanded(!expanded)} className="text-sm font-bold mt-4 hover:underline" style={{ color: RED }} data-testid="tpl-disclaimer-toggle">
          {expanded ? "Read Less" : "Read More"}
        </button>
      )}
    </section>
  );
}

function TCta({ c, onApply, onBrochure }) {
  return (
    <section className="relative overflow-hidden" style={{ background: "linear-gradient(120deg, #0F1115 0%, #1A1D24 60%, #262A33 100%)" }}>
      <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        <div className="text-white">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">{c.headline}</h2>
          <p className="text-sm text-slate-300 mt-2 max-w-md">{c.subtext}</p>
          <div className="mt-6">
            <div className="flex flex-wrap gap-3">
              <button onClick={onApply} data-testid="tpl-cta-apply" className="px-8 py-3 rounded-lg font-bold text-sm text-white hover:opacity-90" style={{ background: RED }}>{c.cta_primary || "Apply Now"}</button>
              <button onClick={onBrochure} data-testid="tpl-cta-brochure" className="px-6 py-3 rounded-lg border border-white/50 font-semibold text-sm text-white hover:bg-white/10 flex items-center gap-2"><Download className="w-4 h-4" /> {c.cta_secondary || "Download Brochure"}</button>
            </div>
          </div>
        </div>
        <div className="relative">
          <div className="rounded-2xl overflow-hidden aspect-[16/9] bg-white/5">{c.image && <img src={c.image} alt={c.image_alt || "Get started"} title={c.image_title || undefined} className="w-full h-full object-cover opacity-80" />}</div>
          <Script className="absolute top-3 left-4 text-2xl font-bold leading-tight" color="#7DD3FC">{c.script_text}</Script>
        </div>
      </div>
    </section>
  );
}

export default function DefaultTemplateRenderer({ sections, onApply, onBrochure, onCallback, device }) {
  useEffect(() => {
    if (!document.getElementById("tpl-font-caveat")) {
      const link = document.createElement("link");
      link.id = "tpl-font-caveat";
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Caveat:wght@600;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  const enabled = (sections || []).filter((s) => {
    if (s.enabled === false) return false;
    if (device === "mobile" && s.hide_mobile) return false;
    if (device === "desktop" && s.hide_desktop) return false;
    return true;
  });
  const out = [];
  const navTabs = [];
  for (const s of enabled) {
    const label = navLabelOf(s);
    if (label) navTabs.push({ id: s.id, label, hideCls: !device ? (s.hide_mobile ? "hidden md:inline-flex" : s.hide_desktop ? "md:hidden" : "") : "" });
  }
  let navInserted = false;
  for (let i = 0; i < enabled.length; i++) {
    const s = enabled[i];
    const c = s.content || {};
    if (!navInserted && navTabs.length >= 2 && navTabs[0].id === s.id) {
      out.push(<TemplateNavTabs key="tpl-nav" tabs={navTabs} />);
      navInserted = true;
    }
    let node = null;
    switch (s.type) {
      case "t_hero": node = <THero c={c} onApply={onApply} onBrochure={onBrochure} onCallback={onCallback} />; break;
      case "t_logos": node = <TLogos c={c} />; break;
      case "t_highlights": node = <THighlights c={c} />; break;
      case "t_certification": node = <TCertification c={c} />; break;
      case "t_curriculum": node = <TCurriculum c={c} onBrochure={onBrochure} />; break;
      case "t_projects": node = <TProjects c={c} />; break;
      case "t_faculty": node = <TFaculty c={c} />; break;
      case "t_testimonials": node = <TTestimonials c={c} />; break;
      case "t_employers": node = <TEmployers c={c} />; break;
      case "t_growth": node = <TGrowth c={c} onApply={onApply} />; break;
      case "t_journey": node = <TJourney c={c} />; break;
      case "t_admission": node = <TAdmission c={c} onApply={onApply} />; break;
      case "t_fee": node = <TFee c={c} onApply={onApply} onBrochure={onBrochure} />; break;
      case "t_compare": node = <TCompare c={c} />; break;
      case "t_about": node = <TAbout c={c} />; break;
      case "t_faq": node = <TFaq c={c} />; break;
      case "t_support": node = <TSupport c={c} />; break;
      case "t_disclaimer": node = <TDisclaimer c={c} />; break;
      case "t_cta": node = <TCta c={c} onApply={onApply} onBrochure={onBrochure} />; break;
      default: break;
    }
    if (node) {
      const hideCls = device ? "" : s.hide_mobile ? "hidden md:block" : s.hide_desktop ? "md:hidden" : "";
      out.push(<div key={s.id} id={`tpl-sec-${s.id}`} className={`scroll-mt-[170px] ${hideCls}`.trim()}>{node}</div>);
    }
  }
  return <div className="bg-white" data-testid="default-template">{out}</div>;
}
